import React, { useState } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import Toast from './Toast'
import Modal from './Modal'

interface ClubSettingsProps {
  clubId: number
  clubName: string
  preferredProvider?: string
  onClose: () => void
  onUpdate: (name: string, provider?: string) => void
}

export default function ClubSettings({
  clubId,
  clubName,
  preferredProvider,
  onClose,
  onUpdate
}: ClubSettingsProps) {
  const [name, setName] = useState(clubName)
  const [provider, setProvider] = useState<string>(preferredProvider || 'amazon')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [showSuccessToast, setShowSuccessToast] = useState(false)

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Club name cannot be empty')
      return
    }

    setSaving(true)
    setError('')

    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          club: {
            name: name.trim(),
            preferred_affiliate_provider: provider
          }
        })
      })

      if (response.ok) {
        const data = await response.json()
        onUpdate(data.club.name, data.club.preferred_affiliate_provider)
        setShowSuccessToast(true)
        setTimeout(() => {
          onClose()
        }, 1500)
      } else {
        const data = await response.json()
        setError(data.errors?.join(', ') || data.error || 'Failed to update club settings')
      }
    } catch (err) {
      setError('Failed to update club settings. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="sm" showCloseButton={true}>
        <div className="p-8">
          <div className="mb-6">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl mb-4">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Club Settings</h2>
            <p className="text-gray-600">Manage your book club settings</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-5">
            {/* Club Name */}
            <div>
              <label htmlFor="clubName" className="block text-sm font-medium text-gray-700 mb-2">
                Club Name
              </label>
              <input
                id="clubName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                placeholder="Enter club name"
              />
            </div>

            {/* Preferred Affiliate Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Book Provider
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Where "Buy Book" buttons will link to by default
              </p>
              <div className="space-y-2">
                <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 has-[:checked]:border-indigo-500 has-[:checked]:bg-indigo-50">
                  <input
                    type="radio"
                    name="provider"
                    value="amazon"
                    checked={provider === 'amazon'}
                    onChange={(e) => setProvider(e.target.value)}
                    className="mr-3 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">Amazon</div>
                    <div className="text-xs text-gray-500">Shop on Amazon.com</div>
                  </div>
                </label>
                <label className="flex items-center p-3 border-2 rounded-lg cursor-not-allowed transition-all bg-gray-50 opacity-60">
                  <input
                    type="radio"
                    name="provider"
                    value="bookshop"
                    disabled
                    className="mr-3 text-gray-400 cursor-not-allowed"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-500 flex items-center gap-2">
                      Bookshop.org
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full font-semibold">
                        Coming Soon
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">Support independent bookstores</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {showSuccessToast && (
        <Toast
          message="Club settings updated successfully!"
          type="success"
          onClose={() => setShowSuccessToast(false)}
        />
      )}
    </>
  )
}
